import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CONFIG } from 'src/config/mainConfig';
import { TeacherOnboardingStatusEnum } from '../../../../interfaces/user.interface';
import ImageCreditsLimitPopup from '../../../../components/Common/ImageCreditsLimitPopup';
import InformNotificationPopup from '../../../../components/Common/InformNotificationPopup';
import { setCurrentStoryStep } from '../../../../helper/helper';
import { IPageDataModel, StoryCreationMethodEnum, StoryLanguageTypeEnum } from '../../../../interfaces/book.interface';
import { setBookId } from '../../../../store/appSlice';
import { AppDispatch, RootState } from '../../../../store/store';
import { setStoryData } from '../../../../store/storySlice';
import TrainingPopup from '../../../trainingvideos/TrainingPopup';
import { StepStoryInformationContainerProps } from '../../models/StepStoryInformationProps.interface';
import { getArtStyles } from '../../services/builderHelpers';
import { updateUser } from '../../services/character.service';
import { handleSubmitFirstBookStep, handleSubmitStepOne } from '../../services/story.service';
import StepStoryDetailsInformation from './StepStoryDetailsInformation';
import StepStoryMainInformation from './StepStoryMainInformation';

export enum StepStoryInformationStep {
  SetNameStep = 'setNameStep',
  SetStoryDataStep = 'setStoryDataStep',
}

export interface StoryPageTextDescriptionValidation {
  is_appropriate: boolean;
  issue_found: string;
  suggested_change: string;
}

export interface BookPagesTextValidation {
  valid: boolean;
  data: { [namePage: string]: StoryPageTextDescriptionValidation };
}

const StepStoryInformationContainer = ({
  setSelectedBookId,
  setLoader,
  setShowLowCreditModal,
  setSelectedStoryConfig,
  setShowDescription,
  setStoryTopic,
  setStoryType,
  setChildName,
  setArtStyle,
  setChildAge,
  setPageTotal,
  setWritingStyle,
  setStoryCreationMethod,
  bookId,
  currentStep,
  childName,
  childAge,
  storyTopic,
  storyType,
  writingStyle,
  pageTotal,
  storyconfig,
  selectedStoryConfig,
  wordRange,
  startStepTwo,
  pageOptions,
  showLowCreditModal,
  storyLanguage,
  setStoryLanguage,
  selectedArtStyle,
  artStyle,
  setSelectedArtStyle,
  storyCreationMethod,
  bookStoryPagesData,
  setBookStoryPagesData,
  onBlurAutosave,
  onSubStepChange,
  initialSubStep,
}: StepStoryInformationContainerProps) => {
  const navigateTo = useNavigate();
  const user = useSelector((state: RootState) => state.user.user);
  const trainingVideos = useSelector((state: RootState) => state.user.trainingVideos);
  const [bookPages, setBookPages] = useState([]);
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [firstStepLoader, setFirstStepLoader] = useState<boolean>(false);
  const [artStyleList, setArtStyleList] = useState([]);
  const bookData = useSelector((state: RootState) => state.app.bookData);
  const dispatch: AppDispatch = useDispatch();
  const [storyInformationStep, setStoryInformationStep] = useState<StepStoryInformationStep | null>(null);
  const initialStepSetRef = useRef(false);
  const bookStoryPagesDataRef = useRef(bookStoryPagesData);
  bookStoryPagesDataRef.current = bookStoryPagesData;

  useEffect(() => {
    if (storyInformationStep) {
      onSubStepChange?.(storyInformationStep);
    }
  }, [storyInformationStep]);

  const [validationErrors, setValidationErrors] = useState<BookPagesTextValidation>({
    valid: true,
    data: {},
  });

  // Sync internal sub-step when parent changes initialSubStep (e.g. OnTourBar back)
  useEffect(() => {
    if (initialStepSetRef.current && initialSubStep) {
      setStoryInformationStep(initialSubStep as StepStoryInformationStep);
    }
  }, [initialSubStep]);

  useEffect(() => {
    if (!initialStepSetRef.current) {
      if (initialSubStep === StepStoryInformationStep.SetStoryDataStep) {
        setStoryInformationStep(StepStoryInformationStep.SetStoryDataStep);
      } else if (currentStep === 1 && bookData?.bookId && !isOnTour) {
        setStoryInformationStep(StepStoryInformationStep.SetStoryDataStep);
      } else {
        setStoryInformationStep(StepStoryInformationStep.SetNameStep);
      }
      initialStepSetRef.current = true;
    }
    if (bookData?.bookId && !isOnTour) {
      const bookDataPagesWithText = Object.values(bookData?.pages || {})?.filter((item) => !!item.text).length;
      const localPagesWithText = Object.values(bookStoryPagesData)?.filter((item) => !!item.text).length;
      if (bookDataPagesWithText !== localPagesWithText) {
        setBookStoryPagesData(mapBookPages(bookData?.pages));
      }
    }
  }, [currentStep, bookData]);

  const mapBookPages = (pages?: { [namePage: string]: IPageDataModel }): { [namePage: string]: IPageDataModel } => {
    return [...Array.from({ length: CONFIG.ONE_STORY_TEXT_PAGES })].reduce(
      (acc: { [namePage: string]: IPageDataModel }, _, index) => {
        const pageKey = `page${index + 1}`;
        acc = {
          ...(acc || {}),
          [pageKey]: {
            text: pages && pages[pageKey.toString()] ? pages[pageKey.toString()].text : '',
            imagePromptCharacterUrl: '',
            uploadedImage: null,
            customCharacterId: null,
            characterOutlineByUser: null,
            images: [],
            scene: '',
            imageURLwebp: '',
            index: pages && pages[pageKey.toString()] ? pages[pageKey.toString()].index : index + 1,
          } as IPageDataModel,
        };
        return acc;
      },
      {} as { [namePage: string]: IPageDataModel },
    );
  };

  const teacherOnboarding = useSelector((state: RootState) => state.user.teacherOnboarding);
  const isOnTour = user?.teacherOnboardingStatus === TeacherOnboardingStatusEnum.BookOnboarding;
  const onTourDefaultsApplied = useRef(false);

  useEffect(() => {
    getArtStyles((list) => {
      setArtStyle(list);
      setArtStyleList(list);

      if (isOnTour && !onTourDefaultsApplied.current && !bookData?.bookId) {
        onTourDefaultsApplied.current = true;
        if (!childName) {
          setChildName('Alex');
          dispatch(setStoryData({ childName: 'Alex' }));
        }
        if (!childAge) {
          setChildAge('3-7-year');
          dispatch(setStoryData({ childAge: '3-7-year' }));
          setSelectedStoryConfig('3-7-year');
        }
        if (!pageTotal) {
          setPageTotal(CONFIG.MAX_LIMIT_PAGES_TO_CREATE.toString());
          dispatch(setStoryData({ pageTotal: CONFIG.MAX_LIMIT_PAGES_TO_CREATE.toString() }));
        }
        const threeDStyle = list.find((s: any) => /3d/i.test(s.name));
        if (threeDStyle && !selectedArtStyle) {
          setArtStyle(threeDStyle.id);
          setSelectedArtStyle(threeDStyle);
          dispatch(setStoryData({ artStyle: threeDStyle }));
        }
      }
    });

    const hasExistingPages = Object.keys(bookStoryPagesData).length > 0;
    if (!hasExistingPages) {
      setBookStoryPagesData(mapBookPages(bookData?.pages));
    }

    return () => {
      localStorage.setItem('pagesData', JSON.stringify(bookStoryPagesDataRef.current));
    };
  }, []);

  useEffect(() => {
    const hasExistingPages = Object.keys(bookStoryPagesData).length > 0;
    if (!hasExistingPages && bookData?.pages && Object.keys(bookData.pages).length > 0) {
      setBookStoryPagesData(mapBookPages(bookData.pages));
    }
  }, []);

  useEffect(() => {
    /**
     * Check Max Allowed Book Page From User Plan
     */
    const filteredRecords = pageOptions.filter((record: { key: string }) => {
      if (user?.isTrialFreeAccess) {
        return parseInt(record.key) === CONFIG.TRIAL.MAX_LIMIT_PAGES_TO_CREATE;
      }
      return (
        parseInt(record.key) <=
        +((user?.plan?.maxBookPage ?? user?.isTrialFreeAccess) ? CONFIG.TRIAL.MAX_LIMIT_PAGES_TO_CREATE : 0)
      );
    });

    setBookPages(filteredRecords);
    if (!isOnTour) {
      setPageTotal(CONFIG.MAX_LIMIT_PAGES_TO_CREATE.toString());
      dispatch(setStoryData({ pageTotal: CONFIG.MAX_LIMIT_PAGES_TO_CREATE.toString() }));
    }
  }, [pageOptions]);

  const onTourStoryDefaultsApplied = useRef(false);
  const ON_TOUR_MARINE_PAGES_BY_AGE: Record<string, Record<string, string>> = {
    '0-2-year': {
      page1: 'I love the ocean.',
      page2: 'Fish swim in the sea.',
      page3: 'Sharks are big and fast.',
      page4: 'Sharks do not hurt people.',
      page5: 'I ride a boat on water.',
      page6: 'I swim with special gear.',
      page7: 'I take pictures of fish.',
      page8: 'I write facts in a book.',
      page9: 'I help keep the ocean clean.',
      page10: 'Dirty water makes animals sick.',
      page11: 'I want sea animals to be safe.',
    },
    '3-7-year': {
      page1: 'When I grow up I want to be a marine biologist.',
      page2: 'That means I study ocean animals and learn about how they live.',
      page3: 'I really like sharks even though some people think they are scary.',
      page4: 'I learned that most sharks do not want to hurt people.',
      page5: 'I would go on a boat and look into the water.',
      page6: 'Maybe I would swim with special gear so I can breathe underwater.',
      page7: 'I would take pictures of fish and sea turtles.',
      page8: 'I would write down facts in a notebook so I do not forget.',
      page9: 'I want to help keep the ocean clean.',
      page10: 'If the ocean is dirty, animals can get sick.',
      page11: 'When I grow up I hope I can help sea animals stay safe.',
    },
    '8-12-year': {
      page1: 'When I grow up I want to be a marine biologist.',
      page2: 'That means I study ocean animals and learn about how they live.',
      page3: 'I really like sharks even though some people think they are scary.',
      page4: 'I learned that most sharks do not want to hurt people.',
      page5: 'I would go on a boat and look into the water.',
      page6: 'Maybe I would swim with special gear so I can breathe underwater.',
      page7: 'I would take pictures of fish and sea turtles.',
      page8: 'I would write down facts in a notebook so I do not forget.',
      page9: 'I want to help keep the ocean clean.',
      page10: 'If the ocean is dirty, animals can get sick.',
      page11: 'When I grow up I hope I can help sea animals stay safe.',
    },
    'adult': {
      page1: 'When I grow up I want to be a marine biologist.',
      page2: 'That means I study ocean animals and learn about how they live.',
      page3: 'I really like sharks even though some people think they are scary.',
      page4: 'I learned that most sharks do not want to hurt people.',
      page5: 'I would go on a boat and look into the water.',
      page6: 'Maybe I would swim with special gear so I can breathe underwater.',
      page7: 'I would take pictures of fish and sea turtles.',
      page8: 'I would write down facts in a notebook so I do not forget.',
      page9: 'I want to help keep the ocean clean.',
      page10: 'If the ocean is dirty, animals can get sick.',
      page11: 'When I grow up I hope I can help sea animals stay safe.',
    },
  };

  useEffect(() => {
    if (isOnTour && !onTourStoryDefaultsApplied.current && storyInformationStep === StepStoryInformationStep.SetStoryDataStep) {
      const hasSelfWrittenText = Object.values(bookStoryPagesData).some((p) => !!p.text);
      if (hasSelfWrittenText) return;

      onTourStoryDefaultsApplied.current = true;
      setStoryCreationMethod(StoryCreationMethodEnum.SELF_WRITTEN);
      dispatch(setStoryData({ storyCreationMethod: StoryCreationMethodEnum.SELF_WRITTEN }));
      setStoryLanguage(StoryLanguageTypeEnum.ENGLISH);
      dispatch(setStoryData({ storyLanguage: StoryLanguageTypeEnum.ENGLISH }));
      setPageTotal(CONFIG.MAX_LIMIT_PAGES_TO_CREATE.toString());
      dispatch(setStoryData({ pageTotal: CONFIG.MAX_LIMIT_PAGES_TO_CREATE.toString() }));

      const ageKey = childAge && ON_TOUR_MARINE_PAGES_BY_AGE[childAge] ? childAge : '3-7-year';
      const tourPageDefaults = ON_TOUR_MARINE_PAGES_BY_AGE[ageKey];

      const pages: { [k: string]: IPageDataModel } = {};
      Object.keys(tourPageDefaults).forEach((key, idx) => {
        pages[key] = {
          text: tourPageDefaults[key],
          imagePromptCharacterUrl: '',
          uploadedImage: null,
          customCharacterId: null,
          characterOutlineByUser: null,
          images: [],
          scene: '',
          imageURLwebp: '',
          index: idx + 1,
        } as IPageDataModel;
      });
      setBookStoryPagesData(pages);
    }
  }, [storyInformationStep, isOnTour]);

  const loadPagesDataText = () => {
    if (isOnTour) return;
    const hasExistingPages =
      Object.keys(bookStoryPagesData).length > 0 && Object.values(bookStoryPagesData).some((item) => !!item.text);
    if (hasExistingPages) {
      return;
    }
    const pagesData = localStorage.getItem('pagesData');
    if (pagesData) {
      const parsedData = JSON.parse(pagesData);
      if (Object.keys(parsedData).length > 0) {
        setBookStoryPagesData(parsedData);
      }
    }
  };

  const submitFirstBookStep = async () => {
    setFirstStepLoader(true);

    const tourPages: { [k: string]: IPageDataModel } = {};
    if (isOnTour) {
      const ageKey = childAge && ON_TOUR_MARINE_PAGES_BY_AGE[childAge] ? childAge : '3-7-year';
      const tourPageDefaults = ON_TOUR_MARINE_PAGES_BY_AGE[ageKey];
      Object.keys(tourPageDefaults).forEach((key, idx) => {
        tourPages[key] = {
          text: tourPageDefaults[key],
          imagePromptCharacterUrl: '',
          uploadedImage: null,
          customCharacterId: null,
          characterOutlineByUser: null,
          images: [],
          scene: '',
          imageURLwebp: '',
          index: idx + 1,
        } as IPageDataModel;
      });
      setBookStoryPagesData(tourPages);
      setStoryCreationMethod(StoryCreationMethodEnum.SELF_WRITTEN);
      dispatch(setStoryData({ storyCreationMethod: StoryCreationMethodEnum.SELF_WRITTEN }));
      setStoryLanguage(StoryLanguageTypeEnum.ENGLISH);
      dispatch(setStoryData({ storyLanguage: StoryLanguageTypeEnum.ENGLISH }));
    }

    const formData = {
      bookId,
      isDraft: true,
      step: currentStep,
      childName,
      childAge,
      writingStyle,
      pageTotal,
      artStyle: selectedArtStyle?.id,
      wordRange: storyconfig[selectedStoryConfig]?.words_total || wordRange,
      ...(isOnTour ? {
        storyCreationMethod: StoryCreationMethodEnum.SELF_WRITTEN,
        storyLanguage: StoryLanguageTypeEnum.ENGLISH,
        pages: tourPages,
      } : {}),
    };

    handleSubmitFirstBookStep(
      formData,
      user,
      bookData,
      setLoader,
      setShowLowCreditModal,
      dispatch,
      setStoryData,
      setBookId,
      setSelectedBookId,
      navigateTo,
      (validation: BookPagesTextValidation) => {
        setValidationErrors(validation);
      },
      () => setStoryInformationStep(StepStoryInformationStep.SetStoryDataStep),
    )
      .then((response) => {})
      .catch((error) => {})
      .finally(() => setFirstStepLoader(false));
  };

  const submitStepOne = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitLoader(true);

    const formData = {
      bookId,
      isDraft: true,
      step: currentStep,
      childName,
      childAge,
      storyTopic,
      storyType,
      writingStyle,
      pageTotal,
      artStyle: selectedArtStyle?.id,
      wordRange: storyconfig[selectedStoryConfig]?.words_total || wordRange,
      storyLanguage,
      storyCreationMethod,
      ...(storyCreationMethod === StoryCreationMethodEnum.SELF_WRITTEN ? { pages: bookStoryPagesData } : {}),
    };

    handleSubmitStepOne(
      formData,
      user,
      bookData,
      setLoader,
      setShowLowCreditModal,
      dispatch,
      setStoryData,
      setBookId,
      setCurrentStoryStep,
      setSelectedBookId,
      startStepTwo,
      navigateTo,
      (validation: BookPagesTextValidation) => {
        setValidationErrors(validation);
      },
    )
      .then((response) => {})
      .catch((error) => {})
      .finally(() => setSubmitLoader(false));
  };

  const [showPopUp, setshowPopUp] = useState(true);

  const onClose = async (): Promise<void> => {
    await updateUser(user, 'screen1');
    setshowPopUp(false);
  };

  return (
    <>
      <form onSubmit={submitStepOne} className="animate__animated animate__slideInRight p-4">
        {showPopUp && !user?.isTeacherMode && user?.showTrainingPopup && user?.showTrainingPopup['screen1'] === true && (
          <TrainingPopup
            videoUrl={currentStep === 1 ? trainingVideos?.STORY_INTRO_VIDEO : null}
            onClose={onClose}
            showScreenName={'screen1'}
          />
        )}
        {storyInformationStep === StepStoryInformationStep.SetNameStep && (
          <StepStoryMainInformation
            childName={childName}
            childAge={childAge}
            artStyle={selectedArtStyle?.id}
            setChildAge={setChildAge}
            setChildName={setChildName}
            setArtStyle={(styleId) => {
              setArtStyle(styleId);
              setSelectedArtStyle(artStyleList.find((style: any) => style.id === styleId) || null);
            }}
            setSelectedStoryConfig={setSelectedStoryConfig}
            storyconfig={storyconfig}
            setShowDescription={setShowDescription}
            submitLoader={firstStepLoader}
            artStyles={artStyleList}
            onContinue={() => submitFirstBookStep()}
          />
        )}
        {storyInformationStep === StepStoryInformationStep.SetStoryDataStep && (
          <StepStoryDetailsInformation
            storyTopic={storyTopic}
            setStoryTopic={setStoryTopic}
            storyType={storyType}
            setStoryType={setStoryType}
            writingStyle={writingStyle}
            setWritingStyle={setWritingStyle}
            storyLanguage={storyLanguage}
            setStoryLanguage={setStoryLanguage}
            storyCreationMethod={storyCreationMethod}
            setStoryCreationMethod={setStoryCreationMethod}
            setShowDescription={setShowDescription}
            pagesDataText={bookStoryPagesData}
            setPagesDataText={(namePage, text) => {
              setBookStoryPagesData({
                ...bookStoryPagesData,
                [namePage]: {
                  ...bookStoryPagesData[namePage],
                  text: text,
                },
              });
            }}
            onLoadPagesDataText={loadPagesDataText}
            storyconfig={storyconfig}
            selectedStoryConfig={selectedStoryConfig}
            onContinue={(event) => submitStepOne(event)}
            onBack={() => setStoryInformationStep(StepStoryInformationStep.SetNameStep)}
            submitLoader={submitLoader}
            validation={validationErrors}
            onBlurAutosave={onBlurAutosave}
          />
        )}
      </form>
      <InformNotificationPopup
        isOpen={showLowCreditModal}
        onClose={() => setShowLowCreditModal(false)}
        title={'Image credits exhausted'}
      >
        <ImageCreditsLimitPopup></ImageCreditsLimitPopup>
      </InformNotificationPopup>
    </>
  );
};

export default StepStoryInformationContainer;
